<template>
    <ObjectiveSectionLayout>
        <!-- header -->
        <CompassIcon slot="icon" size="large" icon="obj-nb-yellow-1" />
        <template slot="title">
            {{ $l10n.__('Définissez votre objectif') }}
        </template>

        <CompassFormItem
            :label="$t.__('objectives.create.section.ObjectiveSectionDefine.category')"
            class="objective-define__field"
        >
            <CompassSelect
                v-model="category"
                class="objective-type__selector"
                :disabled="isDuplicateForm"
            >
                <CompassDropdownSelectOption
                    v-for="objectiveType in objectiveTypes"
                    :key="objectiveType.code"
                    :label="getCategoryLabel(objectiveType.code)"
                    :value="objectiveType.code"
                />
            </CompassSelect>
        </CompassFormItem>

        <CompassFormItem
            :label="$t.__('objectives.create.section.ObjectiveSectionDefine.name')"
            class="objective-define__field"
            :error="errors && errors.get('name')"
        >
            <CompassInput
                v-model="name"
                :placeholder="
                    $t.__('objectives.create.section.ObjectiveSectionDefine.name_placeholder')
                "
                :error="errors && errors.has('name')"
            />
        </CompassFormItem>

        <!-- OWNER-->
        <CompassFormItem
            :label="$t.__('objectives.create.section.ObjectiveSectionDefine.assignee')"
            class="objective-define__field"
            :class="{
                'objective-define__field--multiple': ['staff', 'group'].includes(owner),
            }"
            :error="errors && (errors.get('assignee.type') || errors.get('assignee.id'))"
        >
            <CompassSelect
                v-model="owner"
                class="objective-owner__selector"
                :disabled="!canChooseOwnerWithUpsell"
            >
                <CompassDropdownSelectOption
                    v-for="ownerOption in owners"
                    :key="ownerOption.code"
                    :label="getAssigneeTypeLabel(ownerOption.code)"
                    :value="ownerOption.code"
                    :disabled="hasUpsell(ownerOption.code)"
                >
                    {{ getAssigneeTypeLabel(ownerOption.code) }}
                    <div
                        v-if="hasUpsell(ownerOption)"
                        class="objective-owner__selector--upsell-badge"
                        @click="openFeatureModal(ownerOption.upsell.data)"
                    >
                        <CompassFeatureBadge size="small" variant="upsell" />
                    </div>
                </CompassDropdownSelectOption>
            </CompassSelect>

            <!-- TEAM or STAFF -->
            <CompassSelect
                v-if="['group', 'staff'].includes(owner)"
                :key="`exactOwner-select-${exactOwnerSelectKey}`"
                v-model="exactOwner"
                :filterable="true"
                :actions="actions"
                :error="errors.has('assignee.id')"
                :disabled="!canChooseOwner"
            >
                <CompassDropdownSelectOption
                    v-for="exactOwnerOption in exactOwnersList"
                    :key="exactOwnerOption.value"
                    :label="exactOwnerOption.label"
                    :value="parseInt(exactOwnerOption.value)"
                />
            </CompassSelect>
        </CompassFormItem>

        <!-- ENABLE PRODUCT-->
        <CompassFormItem
            v-if="canSelectItems || hasUpsell(settings.metadata.choose_product)"
            class="objective-define__field objective-define__field-product--switch"
            :class="productSwitchClass"
            label=""
        >
            <CompassTooltip
                :disabled="hasItems"
                placement="bottom-start"
                :content="$t.__('objectives.create.section.ObjectiveSectionDefine.no_product')"
                contentClass="badge-tooltip"
            >
                <div class="objective-define__field-product--switch-content">
                    <CompassSwitch
                        :value="hasItems && openSelectItems"
                        :label="
                            $t.__(
                                'objectives.create.section.ObjectiveSectionDefine.create_for_a_product',
                            )
                        "
                        :disabled="!hasItems || hasUpsell(settings.metadata.choose_product)"
                        orientation="left"
                        @change="toggleProduct"
                    />

                    <div
                        v-if="hasUpsell(settings.metadata.choose_product)"
                        class="objective-define__field-product--switch-upsell-badge"
                        @click="openFeatureModal(settings.metadata.choose_product.upsell.data)"
                    >
                        <CompassFeatureBadge size="small" variant="upsell" />
                    </div>
                </div>
            </CompassTooltip>

            <!-- PRODUCT TYPE-->
            <CompassRadioGroup
                v-if="hasItems && openSelectItems"
                :value="value.itemsType"
                direction="horizontal"
                class="objective-define__field-product--radio-group"
                @input="changeType"
            >
                <CompassRadio
                    v-if="hasProducts"
                    modelValue="product"
                    :label="
                        $t.__(
                            'objectives.create.section.ObjectiveSectionDefine.for_product_or_service',
                        )
                    "
                />
                <CompassRadio
                    v-if="hasCategories"
                    modelValue="category"
                    :label="
                        $t.__(
                            'objectives.create.section.ObjectiveSectionDefine.for_product_category',
                        )
                    "
                />
            </CompassRadioGroup>
        </CompassFormItem>

        <CompassFormItem
            v-if="openSelectItems && isProductItemsType && hasProducts"
            :label="$t.__('objectives.create.section.ObjectiveSectionDefine.product_or_service')"
            class="objective-define__field"
            :error="errors && errors.get('itemId')"
        >
            <CompassAutoComplete
                v-model="itemSelected"
                :results="results"
                :noResultsLabel="
                    $t.__('objectives.create.section.ObjectiveSectionDefine.no_result')
                "
                :placeholder="
                    $t.__(
                        'objectives.create.section.ObjectiveSectionDefine.search_product_or_service',
                    )
                "
                :loading="loading"
                :error="errors && errors.has('itemId')"
                @change="handleSelectedItem"
                @update-search="autoCompleteResults"
            />
        </CompassFormItem>

        <CompassFormItem
            v-if="openSelectItems && isCategoryItemsType && hasCategories"
            :label="$t.__('objectives.create.section.ObjectiveSectionDefine.product_category')"
            class="objective-define__field"
            :error="errors && errors.get('itemCategoryId')"
        >
            <CompassSelect
                v-model="itemId"
                filterable
                :error="errors && errors.has('itemCategoryId')"
            >
                <CompassDropdownSelectOption
                    v-for="category in categoriesList"
                    :key="`item_category-${category.code}`"
                    :label="category.label"
                    :value="parseInt(category.code)"
                />
            </CompassSelect>
        </CompassFormItem>

        <!-- dialog -->
        <CompassDialog
            :visible.sync="showPopup"
            size="small"
            :title="ownerToCreateDialogLabels.title"
            @closed="showPopup = false"
        >
            <div slot="default" class="objective-define-dialog__body">
                <p class="typographies-paragraph-medium-regular">
                    {{ ownerToCreateDialogLabels.content }}
                </p>
            </div>
            <div slot="footer" class="objective-define-dialog__footer">
                <CompassButton
                    :label="$t.__('common.actions.cancel')"
                    type="secondary"
                    @click="showPopup = false"
                />
                <CompassButton
                    :label="ownerToCreateDialogLabels.action"
                    type="primary-destructive"
                    @click="goToSettings"
                />
            </div>
        </CompassDialog>
    </ObjectiveSectionLayout>
</template>

<script>
import ObjectiveSectionLayout from './ObjectiveSectionLayout.vue';
import ObjectiveSectionMixin from './ObjectiveSectionMixin';
import { createNamespacedHelpers } from 'vuex';

const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;

export default {
    components: {
        ObjectiveSectionLayout,
    },
    mixins: [ObjectiveSectionMixin],
    props: {
        isDuplicateForm: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showPopup: false,
            openSelectItems: false,
            canSelectItems: false,
            canChooseOwner: false,
            canChooseOwnerWithUpsell: false,
            loading: false,
            results: [],
            hasProducts: false,
            exactOwnerSelectKey: 0,
            itemSelected: null,
        };
    },
    computed: {
        category: {
            get() {
                return this.value.category;
            },
            set(newCategory) {
                return this.$emit('input', {
                    ...this.value,
                    category: newCategory,
                    needUpdate: this.category !== newCategory,
                    categoryHasChanged: this.category !== newCategory,
                });
            },
        },
        name: {
            get() {
                return this.value.name;
            },
            set(newName) {
                return this.$emit('input', { ...this.value, name: newName, needUpdate: false });
            },
        },
        exactOwner: {
            get() {
                return this.value.exactOwner;
            },
            set(exactOwner) {
                return this.$emit('input', { ...this.value, exactOwner: exactOwner });
            },
        },
        exactOwnersList() {
            return this.settings?.owners.length > 0
                ? this.owners.find((ownerGroup) => ownerGroup.code === this.owner).values || []
                : [];
        },
        categoriesList() {
            return this.settings?.itemCategories?.length > 0 ? this.settings.itemCategories : [];
        },
        owner: {
            get() {
                return this.value.owner;
            },
            set(value) {
                if (value === 'corp') {
                    this.errors.clear('assignee.id');
                }
                this.$emit('update-rules', {
                    rule: 'assignee',
                    disabled: value === 'corp',
                });

                this.exactOwnerSelectKey++;
                this.$emit('input', { ...this.value, owner: value, exactOwner: null });
                this.$trigger('objectiveForm:resetExactOwner');
            },
        },
        owners() {
            return this.settings?.owners;
        },
        objectiveTypes() {
            return this.settings?.categories;
        },
        actions() {
            return [
                {
                    label: this.ownerToCreateLabel,
                    callback: () => {
                        this.showPopup = true;
                    },
                },
            ];
        },
        ownerToCreateLabel() {
            return 'staff' === this.owner
                ? this.$l10n.__('Créer un nouveau collaborateur')
                : this.$l10n.__('Créer une nouvelle équipe');
        },
        ownerToCreateDialogLabels() {
            let dialogLabels = {
                title: this.$l10n.__('Créer un nouveau collaborateur'),
                content: this.$l10n.__(
                    'Attention, vous allez être redirigé vers les réglages pour créer un nouveau collaborateur. En continuant, toutes les informations complétées et votre progression seront perdues.',
                ),
                action: this.$l10n.__('Continuer et créer un nouveau collaborateur'),
            };

            if ('group' === this.owner) {
                dialogLabels = {
                    title: this.$l10n.__('Créer une nouvelle équipe'),
                    content: this.$l10n.__(
                        'Attention, vous allez être redirigé vers les réglages pour créer une nouvelle équipe. En continuant, toutes les informations complétées et votre progession seront perdues.',
                    ),
                    action: this.$l10n.__('Continuer et créer une nouvelle équipe'),
                };
            }

            return dialogLabels;
        },
        itemId: {
            get() {
                return this.value.itemId ?? '';
            },
            set(newItemId) {
                return this.$emit('input', { ...this.value, itemId: newItemId });
            },
        },
        hasCategories() {
            return this.settings?.itemCategories?.length > 0;
        },
        hasItems() {
            return this.hasProducts || this.hasCategories;
        },
        isProductItemsType() {
            return this.value.itemsType === 'product';
        },
        isCategoryItemsType() {
            return this.value.itemsType === 'category';
        },
        productSwitchClass() {
            return this.hasItems && this.openSelectItems
                ? { 'objective-define__field-product--switch-enabled': true }
                : {};
        },
    },
    created() {
        if (this.value.itemId) {
            this.openSelectItems = true;
            this.canSelectItems = true;

            if (this.value.itemsType === 'product' && this.value.itemName) {
                this.itemSelected = {
                    label: this.value.itemName,
                    id: this.value.itemId,
                };
            }
        }

        this.initItems();
        this.handleIfUserCanCreateGroups();
    },
    methods: {
        ...popupPanelMapActions(['open']),
        handleSelectedItem(item) {
            this.itemId = item.id;
        },
        canChooseProduct() {
            return this.settings.metadata.choose_product?.value === true;
        },
        handleIfUserCanCreateGroups() {
            this.canChooseOwner = this.settings.metadata.create_new_group?.value === true;

            this.canChooseOwnerWithUpsell = this.owners.find((owner) => this.hasUpsell(owner))
                ? true
                : this.canChooseOwner;
        },
        goToSettings() {
            switch (this.owner) {
                case 'staff':
                    window.location.assign('/?_f=teamPreferences&action=staffs');
                    break;
                case 'group':
                    window.location.assign('/?_f=teamPreferences&action=groups');
                    break;
            }
        },
        changeType(newItemsType) {
            this.$emit('input', { ...this.value, itemsType: newItemsType, itemId: null });
            this.$emit('update-rules', {
                rule: 'itemId',
                disabled: !(newItemsType === 'product'),
            });
            this.$emit('update-rules', {
                rule: 'itemCategoryId',
                disabled: !(newItemsType === 'category'),
            });
            this.errors.clear('itemId');
            this.errors.clear('itemCategoryId');
        },
        toggleProduct() {
            this.openSelectItems = !this.openSelectItems;

            if (!this.openSelectItems) {
                this.itemId = null;
            }

            this.$emit('update-rules', {
                rule: 'itemId',
                disabled: this.openSelectItems ? !this.isProductItemsType : !this.openSelectItems,
            });
            this.$emit('update-rules', {
                rule: 'itemCategoryId',
                disabled: this.openSelectItems ? !this.isCategoryItemsType : !this.openSelectItems,
            });
        },
        async initItems() {
            await this.fetchAllItems().finally(() => {
                this.hasProducts = true;
                this.canSelectItems = this.canChooseProduct();
                if (this.results?.length === 0) {
                    this.hasProducts = false;
                    this.$emit('input', { ...this.value, itemsType: 'category', itemId: null });
                }
            });
        },
        async autoCompleteResults(query) {
            await this.fetchAllItems(query);
        },
        async fetchAllItems(query) {
            this.loading = true;
            await window.SellsyJS.WebServiceCaller.get(
                `?_f=catalogue&type=&search_type=autocomplete&value=%20&do=autocomplete&action=autocomplete&more%5BincludeDecli%5D=Y&more%5Bthirdid%5D=46&linked='`,
                {
                    value: query,
                    types: ['item', 'service'],
                },
            )
                .then((response) => {
                    this.results = response.data.root.map((item) => ({
                        id: parseInt(item.id.replace(/[^0-9]/g, '')),
                        label: item.tradename,
                        value: item.name,
                        picture: item.pic,
                        disabled: false,
                    }));
                })
                .catch(() => {
                    Push.error(this.$l10n.__('Problème lors de la récupération des données'));
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        hasUpsell(valueToCheck) {
            return valueToCheck && valueToCheck.hasOwnProperty('upsell');
        },
        openFeatureModal(upsellCode) {
            this.open({
                key: 'featureModal',
                props: { feature: upsellCode },
            });
        },
        getCategoryLabel(categoryCode) {
            let categoryLabel = '';
            switch (categoryCode) {
                case 'turnover':
                    categoryLabel = this.$t.__('common.labels.objective.turnover');
                    break;
                case 'forecast_turnover':
                    categoryLabel = this.$t.__('common.labels.objective.forecast_turnover');
                    break;
            }
            return categoryLabel;
        },
        getAssigneeTypeLabel(assigneeTypeCode) {
            let assigneeTypeLabel = '';
            switch (assigneeTypeCode) {
                case 'staff':
                    assigneeTypeLabel = this.$t.__('common.labels.staff');
                    break;
                case 'corp':
                    assigneeTypeLabel = this.$t.__('common.labels.corp');
                    break;
                case 'group':
                    assigneeTypeLabel = this.$t.__('common.labels.team');
                    break;
            }
            return assigneeTypeLabel;
        },
    },
};
</script>

<style lang="scss" scoped>
.objective-define {
    &__field {
        margin-bottom: var(--spacing-5);

        &.objective-define__field--multiple {
            .objective-owner__selector {
                margin-bottom: var(--spacing-2);
            }
        }

        &.objective-define__field-product {
            &--switch {
                display: flex;
                gap: var(--spacing-3);
                margin-bottom: var(--spacing-6);

                &-enabled {
                    margin-bottom: var(--spacing-4);
                }
            }
        }

        &-product--radio-group {
            margin-top: var(--spacing-3);
        }

        &-product--switch-content {
            display: flex;
            gap: var(--spacing-1);
        }
    }

    &-dialog {
        &__body {
            p {
                margin: 0;
            }
        }

        &__footer {
            display: flex;
            justify-content: end;
            gap: var(--spacing-3);

            padding: var(--spacing-4) var(--spacing-5) var(--spacing-5);
        }
    }
}

::v-deep {
    .compass-autocomplete {
        .compass-input {
            width: 100%;
        }
    }

    .compass-feature-badge {
        cursor: pointer;
    }
}
</style>
